import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MasterState } from '@store/states/master.state';
import { MasterGroupedSociety } from '@core/domain/model/master/master-grouped-installations.mode';
import { InstallationStatusCode } from '@core/domain/model/services/individual-installation.model';
import { UserState } from '@store/states/user.state';

const masterSelector = createFeatureSelector<MasterState>('master');
const userSelector = createFeatureSelector<UserState>('user');

export const masterSelectors = {
  societies: createSelector(masterSelector, (state) => state.societies),
  ggccSocieties: createSelector(masterSelector, userSelector, (state, userState) => {
    if (userState.country === 'PT') {
      return state.societies ?? undefined;
    } else {
      return state.societies?.filter((society) => society.clientType === 'GGCC');
    }
  }),
  societiesError: createSelector(masterSelector, (state) => state.errorSocieties),
  installations: createSelector(masterSelector, (state) => state.installations),
  ggccInstallations: createSelector(masterSelector, userSelector, (state, userState) => {
    if (userState.country === 'PT') {
      return state.installations ?? undefined;
    } else {
      return state.installations?.filter((installation) => installation.clientType === 'GGCC');
    }
  }),
  installationsError: createSelector(masterSelector, (state) => state.errorInstallations),
  roadTypes: createSelector(masterSelector, (state) => state.roadTypes),
  roadTypesError: createSelector(masterSelector, (state) => state.errorRoadTypes),
  groupedInstallations: createSelector(masterSelector, (state): MasterGroupedSociety[] => {
    const installations = state.installations;
    const societies = state.societies;
    if (installations == null) {
      return societies?.map((society) => ({ ...society, installations: [] })) ?? [];
    }
    return (
      societies?.map((society) => {
        return {
          ...society,
          installations: installations.filter((installation) => installation.codigoRazonSocial === society.codigo),
        };
      }) ?? []
    );
  }),
  ggccGroupedInstallations: createSelector(masterSelector, (state): MasterGroupedSociety[] => {
    const installations = state.installations?.filter((installation) => installation.clientType === 'GGCC');
    const societies = state.societies?.filter((society) => society.clientType === 'GGCC');
    if (installations == null) {
      return societies?.map((society) => ({ ...society, installations: [] })) ?? [];
    }
    return (
      societies?.map((society) => {
        return {
          ...society,
          installations: installations.filter((installation) => installation.codigoRazonSocial === society.codigo),
        };
      }) ?? []
    );
  }),
  isAdmin: createSelector(masterSelector, (state) => !!state.installations?.find((installation) => installation.role === 'ADMIN')),
  selfConsumptions: createSelector(masterSelector, (state) => state.selfConsumptions),
  selfConsumptionsError: createSelector(masterSelector, (state) => state.errorSelfConsumptions),
  cups: createSelector(masterSelector, (state) => state.cups),
  cupsError: createSelector(masterSelector, (state) => state.errorCups),
  subsectionType: createSelector(masterSelector, (state) => state.subsectionType),
  subsectionTypeError: createSelector(masterSelector, (state) => state.errorSubsectionType),
  installationType: createSelector(masterSelector, (state) => state.installationType),
  installationTypeError: createSelector(masterSelector, (state) => state.errorInstallationType),
  activeInstallations: createSelector(masterSelector, (state) =>
    state.installations?.filter((installation) => installation.contractStatus === InstallationStatusCode.FORMALIZED)
  ),
  activeGroupedInstallations: createSelector(masterSelector, (state): MasterGroupedSociety[] => {
    const installations = state.installations?.filter((installation) => installation.contractStatus === InstallationStatusCode.FORMALIZED);
    const societies = state.societies?.filter((society) =>
      installations?.map((installation) => installation.codigoRazonSocial).includes(society.codigo)
    );
    if (installations == null) {
      return societies?.map((society) => ({ ...society, installations: [] })) ?? [];
    }
    return (
      societies?.map((society) => {
        return {
          ...society,
          installations: installations.filter((installation) => installation.codigoRazonSocial === society.codigo),
        };
      }) ?? []
    );
  }),
};
